import { apiClient } from './client'
import type {
  Hospital,
  HospitalListResponse,
  HospitalSearchParams,
  HospitalUser,
  SSOConfiguration,
  SSOConfigResponse,
} from '@/types/hospital'

export const hospitalsApi = {
  /**
   * Get list of hospitals with optional search and filters
   */
  async getHospitals(params?: HospitalSearchParams) {
    const response = await apiClient.get<{ success: boolean; data: HospitalListResponse }>(
      '/hospitals',
      { params }
    )
    return response.data
  },

  /**
   * Get hospital by ID
   */
  async getHospital(id: string) {
    const response = await apiClient.get<{ success: boolean; data: Hospital }>(`/hospitals/${id}`)
    return response.data
  },

  /**
   * Search hospitals by name (typeahead)
   */
  async searchHospitals(query: string, limit: number = 10) {
    const response = await apiClient.get<{ success: boolean; data: Hospital[] }>(
      '/hospitals/search',
      { params: { q: query, limit } }
    )
    return response.data
  },

  /**
   * Fetch multiple hospitals by IDs
   */
  async getBulkHospitals(ids: string[]) {
    const response = await apiClient.post<{ success: boolean; data: Hospital[] }>(
      '/hospitals/bulk',
      { ids }
    )
    return response.data
  },

  /**
   * Get list of unique states that have hospitals
   */
  async getStates() {
    const response = await apiClient.get<{ success: boolean; data: string[] }>('/hospitals/states')
    return response.data
  },

  /**
   * Get all users with access to a specific hospital
   */
  async getHospitalUsers(hospitalId: string) {
    const response = await apiClient.get<{ success: boolean; data: HospitalUser[] }>(
      `/hospitals/${hospitalId}/users`
    )
    return response.data
  },

  /**
   * Update a hospital
   */
  async updateHospital(id: string, data: Partial<Hospital>) {
    const response = await apiClient.patch<{ success: boolean; data: Hospital }>(
      `/hospitals/${id}`,
      data
    )
    return response.data
  },

  /**
   * Get all markets for dropdown selection
   */
  async getMarkets() {
    const response = await apiClient.get<{
      success: boolean
      data: Array<{ id: string; name: string }>
    }>('/hospitals/markets')
    return response.data
  },

  /**
   * Get SSO configuration for a hospital
   */
  async getSSOConfig(hospitalId: string) {
    const response = await apiClient.get<{ success: boolean; data: SSOConfigResponse }>(
      `/hospitals/${hospitalId}/sso`
    )
    return response.data
  },

  /**
   * Update SSO configuration for a hospital
   */
  async updateSSOConfig(hospitalId: string, config: SSOConfiguration) {
    const response = await apiClient.put<{ success: boolean; data: SSOConfigResponse }>(
      `/hospitals/${hospitalId}/sso`,
      config
    )
    return response.data
  },

  /**
   * Delete SSO configuration from a hospital
   */
  async deleteSSOConfig(hospitalId: string) {
    const response = await apiClient.delete<{ success: boolean; data: { message: string } }>(
      `/hospitals/${hospitalId}/sso`
    )
    return response.data
  },
}
